import React, { useRef, useEffect, useState } from 'react';
import { Button, ButtonProps } from '@mantine/core';

interface MagneticButtonProps extends ButtonProps {
  magneticStrength?: number;
  magneticRadius?: number;
  children: React.ReactNode;
  onClick?: () => void;
}

export const MagneticButton: React.FC<MagneticButtonProps> = ({
  magneticStrength = 0.4,
  magneticRadius = 100,
  children,
  onClick,
  ...buttonProps
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [transform, setTransform] = useState('translate(0px, 0px)');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = button.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const deltaX = e.clientX - centerX;
      const deltaY = e.clientY - centerY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance <= magneticRadius) {
        const force = (magneticRadius - distance) / magneticRadius;
        const moveX = deltaX * force * magneticStrength;
        const moveY = deltaY * force * magneticStrength;
        
        setTransform(`translate(${moveX}px, ${moveY}px)`);
        setIsHovered(true);
      } else {
        setTransform('translate(0px, 0px)');
        setIsHovered(false);
      }
    };

    // Add event listener to document for global mouse tracking
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [magneticStrength, magneticRadius]);

  return (
    <Button
      ref={buttonRef}
      onClick={onClick}
      {...buttonProps}
      style={{
        transform: transform,
        transition: isHovered ? 'none' : 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        filter: isHovered ? 'drop-shadow(0 0 20px rgba(102, 126, 234, 0.6)) brightness(1.1)' : 'none',
        cursor: 'pointer',
        position: 'relative',
        zIndex: 10,
        ...buttonProps.style
      }}
    >
      {children}
    </Button>
  );
};

export default MagneticButton; 